import { z } from "zod";
import type { Hit } from "./beats.js";

/**
 * A photo toured by our own camera (2026-09-27, docs/30 "one picture"): the photo is read once, when it is described on
 * upload, for where the things worth looking at are (`Region`); the script chooses a few of them and what to do at each
 * (`TourStop`); the renderer flies over the untouched photo to each in turn, draws the effect and the words, and puts a
 * sound on each arrival. One cheap read per photo, and nothing to pay per video: no edited picture, no clip.
 */

/** Where something is in a photo: [ymin, xmin, ymax, xmax], each 0..1000 of the photo's height or width. */
export const Box = z.tuple([z.number().min(0).max(1000), z.number().min(0).max(1000), z.number().min(0).max(1000), z.number().min(0).max(1000)]);
export type Box = z.infer<typeof Box>;

/** A thing worth looking at in a photo: an object, a detail inside one, or writing. */
export const Region = z.object({ label: z.string().min(1).max(60), box: Box, kind: z.enum(["object", "detail", "text"]) });
export type Region = z.infer<typeof Region>;

/**
 * What happens when the camera arrives: it only looks, with the thing outlined; it scans down it line by line, ticking
 * each (writing, a document, a list); something rises from it (steam, warmth, sparkle); or a word is flagged on it.
 */
export const TourEffect = z.enum(["look", "scan", "rise", "flag"]);
export type TourEffect = z.infer<typeof TourEffect>;

export const TOUR_STOPS_MAX = 3;

/** One stop of a tour: which region of the shot's photo, what to do there, and the few words said on screen. */
export const TourStop = z.object({ region: z.number().int().min(0).max(11), effect: TourEffect, say: z.string().max(40).nullable() });
export type TourStop = z.infer<typeof TourStop>;

/** A stop as the renderer gets it: the region's box, resolved from the photo. */
export const TourStopView = z.object({ box: Box, effect: TourEffect, say: z.string().max(40).nullable() });
export type TourStopView = z.infer<typeof TourStopView>;

/** The model's guide to tours, in the script's system prompt. */
export const TOUR_GUIDE = `A shot from one of the owner's photos is toured by our camera: set motion "tour" and a "tour" of 1 to ${TOUR_STOPS_MAX} stops. Each stop names a region of that photo by its "n" as the photo's regions list gives it (they are numbered from 0), an effect and "say", at most 40 characters shown on screen at that stop (or null). Effects: "look" (the camera goes to it and outlines it), "scan" (for writing, a document or a list: a scanner runs down it ticking each line), "rise" (steam, warmth or sparkle rising from it: a cup, food, a machine), "flag" (a short word slammed on it, for what is wrong or what matters). Order the stops as the story needs; the camera starts on the whole photo. Words held to the business page like every other.`;

/**
 * When a tour does what, in frames of its shot: the whole photo first, then each stop in turn (the camera flies for
 * `FLY` frames and arrives at `arrive`), and back to the whole photo at the end when there is time. The renderer draws
 * from this and the worker puts the sounds on it, so the two agree.
 */
export const TOUR_FLY = 16;
export function tourBeats(frames: number, stops: number) {
  const lead = Math.min(20, Math.round(frames * 0.12));
  const back = frames >= 150 ? 22 : 0;
  const seg = Math.max(TOUR_FLY + 12, (frames - lead - back) / Math.max(1, stops));
  const arrive = (i: number) => lead + i * seg + TOUR_FLY;
  return { lead, seg, back, arrive, leave: (i: number) => Math.min(frames - back, lead + (i + 1) * seg), rows: 5, scanRow: (i: number, row: number) => arrive(i) + 4 + ((row + 0.6) / 5) * Math.max(12, seg - TOUR_FLY - 14) };
}

/** Every sound a tour makes: a whoosh into each stop, a pop for each line scanned, a thud for a word flagged. */
export function tourHits(stops: ReadonlyArray<{ effect: TourEffect }>, frames: number): Hit[] {
  const b = tourBeats(frames, stops.length);
  const hits: Hit[] = [];
  stops.forEach((s, i) => {
    hits.push({ frame: b.arrive(i), kind: "whoosh" });
    if (s.effect === "scan") for (let r = 0; r < b.rows; r++) hits.push({ frame: b.scanRow(i, r), kind: "pop", step: r });
    if (s.effect === "flag") hits.push({ frame: b.arrive(i) + 8, kind: "thud" });
  });
  return hits.filter((h) => h.frame < frames).sort((a, c) => a.frame - c.frame);
}
